import apiClient from './apiClient';

// ============================================================================
// Types
// ============================================================================

/**
 * Response from GET /api/organizations/{id}/subscriptions/portal
 * or GET /api/talents/{id}/subscriptions/portal
 */
export interface PortalResponse {
	url: string;
}

/**
 * Response from GET /api/organizations/{id}/subscriptions/current
 * or GET /api/talents/{id}/subscriptions/current
 */
export interface CurrentSubscription {
	plan: string; // "Free" | "Plus" | "Pro"
	status: string; // "Active" | "Canceled" | "PastDue"
	isPaid: boolean;
	currentPeriodStart: string | null;
	currentPeriodEnd: string | null;
	cancelAtPeriodEnd: boolean;
	maxActiveContracts: number;
	activeContractsCount: number;
	remainingContracts: number;
	canCreateContract: boolean;
	usagePercentage: number;
}

// ============================================================================
// Service
// ============================================================================

const subscriptionService = {
	/**
	 * Gets the Stripe Customer Portal URL for an organization.
	 * @param organizationId - The organization ID
	 * @param returnUrl - URL to redirect back to after portal session
	 * @returns The Stripe portal URL
	 */
	getOrganizationPortalUrl: async (
		organizationId: string,
		returnUrl?: string,
	): Promise<string> => {
		const params = new URLSearchParams();
		if (returnUrl) {
			params.append('returnUrl', returnUrl);
		}
		const queryString = params.toString();
		const url = `/api/organizations/${organizationId}/subscriptions/portal${queryString ? `?${queryString}` : ''}`;

		const response = await apiClient.get<PortalResponse>(url);
		return response.data.url;
	},

	/**
	 * Gets the Stripe Customer Portal URL for a talent.
	 * @param talentId - The talent ID
	 * @param returnUrl - URL to redirect back to after portal session
	 * @returns The Stripe portal URL
	 */
	getTalentPortalUrl: async (talentId: string, returnUrl?: string): Promise<string> => {
		const params = new URLSearchParams();
		if (returnUrl) {
			params.append('returnUrl', returnUrl);
		}
		const queryString = params.toString();
		const url = `/api/talentusers/me/subscriptions/portal${queryString ? `?${queryString}` : ''}`;

		const response = await apiClient.get<PortalResponse>(url);
		return response.data.url;
	},

	/**
	 * Gets current subscription details for an organization.
	 * @param organizationId - The organization ID
	 * @returns Current subscription data
	 */
	getOrganizationSubscription: async (organizationId: string): Promise<CurrentSubscription> => {
		const response = await apiClient.get<CurrentSubscription>(
			`/api/organizations/${organizationId}/subscriptions/current`,
		);
		return response.data;
	},

	/**
	 * Syncs the organization's subscription with Stripe.
	 * Creates a Free subscription if none exists.
	 * Should be called before accessing subscription data or portal.
	 * @param organizationId - The organization ID
	 * @returns Updated subscription data
	 */
	syncOrganizationSubscription: async (organizationId: string): Promise<CurrentSubscription> => {
		const response = await apiClient.post<CurrentSubscription>(
			`/api/organizations/${organizationId}/subscriptions/sync`,
		);
		return response.data;
	},

	/**
	 * Gets current subscription details for a talent.
	 * @param talentId - The talent ID
	 * @returns Current subscription data
	 */
	getTalentSubscription: async (talentId: string): Promise<CurrentSubscription> => {
		const response = await apiClient.get<CurrentSubscription>(
			`/api/talentusers/me/subscriptions/current`,
		);
		return response.data;
	},

	/**
	 * Syncs the talent's subscription with Stripe.
	 * Creates a Free subscription if none exists.
	 * Should be called before accessing subscription data or portal.
	 * @param talentId - The talent ID
	 * @returns Updated subscription data
	 */
	syncTalentSubscription: async (talentId: string): Promise<CurrentSubscription> => {
		const response = await apiClient.post<CurrentSubscription>(
			`/api/talentusers/me/subscriptions/sync`,
		);
		return response.data;
	},

	/**
	 * Redirects user to Stripe Customer Portal for an organization.
	 * This performs a full page redirect.
	 * @param organizationId - The organization ID
	 * @param returnUrl - URL to redirect back to (defaults to current page)
	 */
	redirectToOrganizationPortal: async (
		organizationId: string,
		returnUrl?: string,
	): Promise<void> => {
		const url = await subscriptionService.getOrganizationPortalUrl(
			organizationId,
			returnUrl ?? window.location.href,
		);
		window.location.href = url;
	},

	/**
	 * Redirects talent to Stripe Customer Portal.
	 * @param talentId - The talent ID
	 * @param returnUrl - URL to redirect back to (defaults to current page)
	 */
	redirectToTalentPortal: async (talentId: string, returnUrl?: string): Promise<void> => {
		const url = await subscriptionService.getTalentPortalUrl(
			talentId,
			returnUrl ?? window.location.href,
		);
		window.location.href = url;
	},
};

export default subscriptionService;
